import { convertSearchParamsToObject } from "helpers";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { Button, RadioButtonsFilterField, RangeFilterField } from "ui";

import { useGetResellersFilterLimits } from "../../../apis";
import {
  EMPTY_RESELLERS_FILTER_FORM_VALUES,
  AGE_FROM_FILTER_SEARCH_PARAMETER,
  AGE_TO_FILTER_SEARCH_PARAMETER,
  EARNINGS_FROM_FILTER_SEARCH_PARAMETER,
  EARNINGS_TO_FILTER_SEARCH_PARAMETER,
  GENDER_FILTER_SEARCH_PARAMETER,
  SALES_FROM_FILTER_SEARCH_PARAMETER,
  SALES_TO_FILTER_SEARCH_PARAMETER,
  CANCELED_FROM_FILTER_SEARCH_PARAMETER,
  CANCELED_TO_FILTER_SEARCH_PARAMETER,
  DELIVERED_FROM_FILTER_SEARCH_PARAMETER,
  DELIVERED_TO_FILTER_SEARCH_PARAMETER,
  RETURNED_FROM_FILTER_SEARCH_PARAMETER,
  RETURNED_TO_FILTER_SEARCH_PARAMETER,
  DELETE_STATUS_FILTER_SEARCH_PARAMETER,
  ResellersFilterGenderValues,
  VERIFICATION_FILTER_SEARCH_PARAMETER,
  ResellersFilterVerificationValues,
  ResellersFilterDeleteStatusValues
} from "../../../constants";
import { getResellersFilterNewSearchParams } from "../../../helpers";
import {
  ResellersFilterFormProps,
  ResellersFilterFormValues
} from "./ResellersFilterForm.types";

import styles from "./ResellersFilterForm.module.scss";

const ResellersFilterForm = ({ closeFilter }: ResellersFilterFormProps) => {
  const { filterLimits, isLoading } = useGetResellersFilterLimits();
  const [searchParams, setSearchParams] = useSearchParams();

  const formMethods = useForm<ResellersFilterFormValues>({
    defaultValues: EMPTY_RESELLERS_FILTER_FORM_VALUES
  });

  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    const newValues = convertSearchParamsToObject(searchParams);

    const handledNewValues = {
      ...EMPTY_RESELLERS_FILTER_FORM_VALUES,
      ...newValues
    };

    reset(handledNewValues);
  }, [searchParams]);

  const clearFilterForm = () => {
    searchParams.delete(VERIFICATION_FILTER_SEARCH_PARAMETER);
    searchParams.delete(GENDER_FILTER_SEARCH_PARAMETER);
    searchParams.delete(AGE_FROM_FILTER_SEARCH_PARAMETER);
    searchParams.delete(AGE_TO_FILTER_SEARCH_PARAMETER);
    searchParams.delete(EARNINGS_TO_FILTER_SEARCH_PARAMETER);
    searchParams.delete(EARNINGS_FROM_FILTER_SEARCH_PARAMETER);
    searchParams.delete(SALES_FROM_FILTER_SEARCH_PARAMETER);
    searchParams.delete(SALES_TO_FILTER_SEARCH_PARAMETER);
    searchParams.delete(CANCELED_FROM_FILTER_SEARCH_PARAMETER);
    searchParams.delete(CANCELED_TO_FILTER_SEARCH_PARAMETER);
    searchParams.delete(DELIVERED_FROM_FILTER_SEARCH_PARAMETER);
    searchParams.delete(DELIVERED_TO_FILTER_SEARCH_PARAMETER);
    searchParams.delete(RETURNED_FROM_FILTER_SEARCH_PARAMETER);
    searchParams.delete(RETURNED_TO_FILTER_SEARCH_PARAMETER);
    searchParams.delete(DELETE_STATUS_FILTER_SEARCH_PARAMETER);

    setSearchParams(searchParams);
    reset();
  };

  const submitFilterForm = (body: ResellersFilterFormValues) => {
    const existingParams = convertSearchParamsToObject(searchParams);
    const newParams = getResellersFilterNewSearchParams(body);

    setSearchParams(
      createSearchParams({
        ...existingParams,
        ...newParams
      })
    );

    closeFilter();
  };

  return (
    <FormProvider {...formMethods}>
      <form
        id={"RESELLER_FORM_ID"}
        className={styles.resellersFilterForm}
        onSubmit={handleSubmit(submitFilterForm)}
      >
        {isLoading && (
          <div className={styles.resellersFilterForm__loader}>
            <ClipLoader size={30} color="#fb3973" />
          </div>
        )}

        {filterLimits && (
          <>
            <div className={styles.resellersFilterForm__header}>
              <div className={styles.resellersFilterForm__headerTitle}>
                {t("Filter by")}
              </div>
              <div
                onClick={clearFilterForm}
                className={styles.resellersFilterForm__headerClear}
              >
                {t("Clear all filters")}
              </div>
            </div>

            <RadioButtonsFilterField
              title={t("Verification")}
              fieldKey={VERIFICATION_FILTER_SEARCH_PARAMETER}
              options={[
                {
                  label: t("All"),
                  value: ResellersFilterVerificationValues.All,
                  checkedByDefault: true
                },
                {
                  label: t("Verified"),
                  value: ResellersFilterVerificationValues.Verified
                },
                {
                  label: t("Unverified"),
                  value: ResellersFilterVerificationValues.Unverified
                }
              ]}
            />
            <div className={styles.resellersFilterForm__line} />

            <RadioButtonsFilterField
              title={t("Status")}
              fieldKey={DELETE_STATUS_FILTER_SEARCH_PARAMETER}
              options={[
                {
                  label: t("Active"),
                  value: ResellersFilterDeleteStatusValues.Active,
                  checkedByDefault: true
                },
                {
                  label: t("Deleted"),
                  value: ResellersFilterDeleteStatusValues.Deleted
                },
                {
                  label: t("All"),
                  value: ResellersFilterDeleteStatusValues.All
                }
              ]}
            />

            <div className={styles.resellersFilterForm__line} />

            <RadioButtonsFilterField
              title={t("Gender")}
              fieldKey={GENDER_FILTER_SEARCH_PARAMETER}
              options={[
                {
                  label: t("All"),
                  value: ResellersFilterGenderValues.All,
                  checkedByDefault: true
                },
                { label: t("Male"), value: ResellersFilterGenderValues.MALE },
                {
                  label: t("Female"),
                  value: ResellersFilterGenderValues.FEMALE
                }
              ]}
            />

            <div className={styles.resellersFilterForm__line} />

            <RangeFilterField
              title={t("Age")}
              fromFieldKey={AGE_FROM_FILTER_SEARCH_PARAMETER}
              toFieldKey={AGE_TO_FILTER_SEARCH_PARAMETER}
              minTotal={filterLimits.minAge}
              maxTotal={filterLimits.maxAge}
              withAccordion
            />

            <div className={styles.resellersFilterForm__line} />

            <RangeFilterField
              title={`${t("Earnings")} (FCFA)`}
              fromFieldKey={EARNINGS_FROM_FILTER_SEARCH_PARAMETER}
              toFieldKey={EARNINGS_TO_FILTER_SEARCH_PARAMETER}
              minTotal={filterLimits.minTotalEarnings}
              maxTotal={filterLimits.maxTotalEarnings}
              withAccordion
            />

            <div className={styles.resellersFilterForm__line} />

            <RangeFilterField
              title={`${t("Total Sales")} (FCFA)`}
              fromFieldKey={SALES_FROM_FILTER_SEARCH_PARAMETER}
              toFieldKey={SALES_TO_FILTER_SEARCH_PARAMETER}
              minTotal={filterLimits.minTotalSales}
              maxTotal={filterLimits.maxTotalSales}
              withAccordion
            />

            <div className={styles.resellersFilterForm__line} />

            <RangeFilterField
              title={t("Canceled Orders")}
              fromFieldKey={CANCELED_FROM_FILTER_SEARCH_PARAMETER}
              toFieldKey={CANCELED_TO_FILTER_SEARCH_PARAMETER}
              minTotal={filterLimits.minNumberOfCanceledOrders}
              maxTotal={filterLimits.maxNumberOfCanceledOrders}
              withAccordion
            />

            <div className={styles.resellersFilterForm__line} />

            <RangeFilterField
              title={t("Delivered Orders")}
              fromFieldKey={DELIVERED_FROM_FILTER_SEARCH_PARAMETER}
              toFieldKey={DELIVERED_TO_FILTER_SEARCH_PARAMETER}
              minTotal={filterLimits.minNumberOfDeliveredOrders}
              maxTotal={filterLimits.maxNumberOfDeliveredOrders}
              withAccordion
            />

            <div className={styles.resellersFilterForm__line} />

            <RangeFilterField
              title={t("Returned Orders")}
              fromFieldKey={RETURNED_FROM_FILTER_SEARCH_PARAMETER}
              toFieldKey={RETURNED_TO_FILTER_SEARCH_PARAMETER}
              minTotal={filterLimits.minNumberOfReturnedOrders}
              maxTotal={filterLimits.maxNumberOfReturnedOrders}
              withAccordion
            />

            <div className={styles.resellersFilterForm__line} />

            <Button
              type="submit"
              className={styles.resellersFilterForm__button}
              loading={false}
            >
              {t("Apply")}
            </Button>
          </>
        )}
      </form>
    </FormProvider>
  );
};

export default ResellersFilterForm;
